import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  alpha,
  Box,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { RADIUS } from "../../theme/mixins";
import { docKindFor, displayFileName, FileTypeIcon, Icon } from "../ui/icons";
import CustomList from "../CustomList";
import DocumentListSkeleton from "./DocumentListSkeleton";
import { userDocumentService, type UserDocumentResponse } from "../../services";
import type { PagedResult } from "../../services/types";
import { useCurrentUserId } from "../../hooks/useCurrentUserId";
import { usePermissions } from "../../hooks/usePermissions";
import { formatSizeInMB, formatDate } from "../../utils/formatUtils";
import SearchBar from "../SearchBar";
import { useCompanyContext } from "../../contexts/CompanyContext";
import HistoryDrawer, { type HistoryItem } from "../HistoryDrawer";
import { workflowService, type WorkflowHistoryDto } from "../../services";
import { Snackbar, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { navigateToWorkflow, workflowRoutes } from "../../utils/workflowRouter";

interface DocumentsViewProps {
  renderActionButtons?: (document: UserDocumentResponse) => React.ReactNode;
  screenName?: string; // Optional screen name for permission context
  refreshTrigger?: number; // Optional trigger to refresh documents
  // Optional history-item click interceptor. Return true to indicate the parent fully
  // handled the click (skips the default fetch + navigateToWorkflow behavior below).
  onHistoryItemClick?: (item: HistoryItem) => boolean | void;
  // If provided, replaces the default userDocumentService fetcher. Useful for pages
  // that source documents from a different collection (e.g. TechnicalEstimateDocuments).
  fetchDocumentsOverride?: (
    page: number,
    pageSize: number,
    searchTerm: string,
    companyId: string,
  ) => Promise<PagedResult<UserDocumentResponse>>;
}

const DocumentsView: React.FC<DocumentsViewProps> = ({
  renderActionButtons,
  screenName,
  refreshTrigger,
  onHistoryItemClick,
  fetchDocumentsOverride,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const userId = useCurrentUserId();
  const { canRead } = usePermissions();
  const [documents, setDocuments] = useState<UserDocumentResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isSmallerScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const { selectedCompanyId, isCompanyLoaded } = useCompanyContext();
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [apiHistoryItems, setApiHistoryItems] = useState<HistoryItem[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showCompanyHistory, setShowCompanyHistory] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const hasMore = documents.length < totalItems;

  // Permission checks based on screen context
  // const canDeleteDocuments = screenName ? canDelete(screenName) : false; // Default to true if no screenName provided
  const canRefreshDocuments = screenName ? canRead(screenName) : true; // Default to true if no screenName provided

  const fetchDocuments = useCallback(async (page: number, append: boolean) => {
    // selectedCompanyId starts empty and resolves asynchronously once CompanyContext
    // finishes loading — fetching before that would fire a call with the wrong company
    // filter, immediately followed by a second, correct call once it resolves.
    if (!isCompanyLoaded) return;
    if (!fetchDocumentsOverride && !userId) return;
    if (searchTerm.length > 0 && searchTerm.length < 3) return;

    append ? setIsLoadingMore(true) : setIsLoading(true);
    setError(null);

    try {
      const companyIdToSend =
        selectedCompanyId !== "all" ? selectedCompanyId : undefined;

      const response = fetchDocumentsOverride
        ? await fetchDocumentsOverride(page, itemsPerPage, searchTerm, companyIdToSend ?? '')
        : await userDocumentService.getByUserId(
            page,
            itemsPerPage,
            searchTerm || undefined,
            companyIdToSend,
          );

      setDocuments((prev) => append ? [...prev, ...response.items] : response.items);
      setTotalItems(response.totalCount);
    } catch (err) {
      console.error("Error fetching documents:", err);
      setError(
        err instanceof Error
          ? err.message
          : t('components.documentsView.loadError'),
      );
    } finally {
      append ? setIsLoadingMore(false) : setIsLoading(false);
    }
  }, [fetchDocumentsOverride, userId, itemsPerPage, searchTerm, selectedCompanyId, isCompanyLoaded]);

  // Fetch history from API when drawer opens
  useEffect(() => {
    const fetchHistory = async () => {
      if (!historyDrawerOpen) return;

      setIsLoadingHistory(true);
      try {
        let companyIdParam: string | null = null;

        if (
          showCompanyHistory &&
          selectedCompanyId &&
          selectedCompanyId !== "all"
        ) {
          companyIdParam = selectedCompanyId;
        }

        const response = await workflowService.getHistory(
          companyIdParam,
          showCompanyHistory,
        );

        const mappedItems: HistoryItem[] = response.map(
          (item: WorkflowHistoryDto) => ({
            id: item.id,
            name: item.title,
            timestamp: item.timestamp,
            workflow: item.workflow,
            workflowType: item.workflowType,
            companyId: item.companyId,
            isMyDocument: item.isMyDocument,
            userName: item.userName,
            jobId: item.jobId,
          }),
        );

        setApiHistoryItems(mappedItems);
      } catch (error) {
        console.error("Failed to fetch workflow history:", error);
        setApiHistoryItems([]);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    fetchHistory();
  }, [historyDrawerOpen, showCompanyHistory, selectedCompanyId, screenName]);

  // Initial load and reset on search / company / refresh changes
  useEffect(() => {
    setCurrentPage(1);
    fetchDocuments(1, false);
  }, [searchTerm, selectedCompanyId, refreshTrigger, fetchDocuments]);

  // Load next page when currentPage increments beyond 1
  useEffect(() => {
    if (currentPage > 1) {
      fetchDocuments(currentPage, true);
    }
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep latest flags in refs so the observer callback always reads fresh values
  // without needing to be recreated (recreating it re-fires on current intersection
  // state, causing a fetch cascade unrelated to actual scrolling).
  const hasMoreRef = useRef(hasMore);
  const isLoadingRef = useRef(isLoading);
  const isLoadingMoreRef = useRef(isLoadingMore);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  // IntersectionObserver — increment page when sentinel scrolls into view.
  // Created once (not recreated per state change) so it never re-fires on its own;
  // the ref reads above keep its callback guard fresh regardless.
  const observerRef = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMoreRef.current &&
          !isLoadingRef.current &&
          !isLoadingMoreRef.current
        ) {
          setCurrentPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    observerRef.current = observer;
    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, []);

  // Re-check the sentinel's current visibility once a fetch cycle finishes. This
  // matters when the list sits in a fixed-height scroll container that hasn't
  // started overflowing yet — the sentinel can stay continuously visible across a
  // page load with no intersection-ratio change, so the observer alone would never
  // fire again and loading would stall short of totalItems despite room to keep going.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const observer = observerRef.current;
    if (!sentinel || !observer) return;
    observer.unobserve(sentinel);
    observer.observe(sentinel);
  }, [isLoading, isLoadingMore, hasMore]);

  // Transform documents for display. `name` is the cleaned, human-readable
  // title (no storage UUID suffix); the untouched document stays on
  // `originalDoc` for downloads and API calls (UI/UX audit 2.4.5).
  const transformedDocuments = documents.map((doc) => ({
    id: doc.id,
    name: displayFileName(doc.tittle) || t('components.documentsView.unnamedDocument'),
    size: formatSizeInMB(doc.documentSize),
    date: doc.createdDate ? formatDate(doc.createdDate) : t('common.unknownDate'),
    // The real file type comes from the extension, not from a substring match
    // on the URL (UI/UX audit 2.3.4).
    type: docKindFor(doc.tittle || doc.documentLink),
    companyName: doc.companyName || t('components.documentsView.noCompany'),
    originalDoc: doc, // Keep reference to original document
  }));

  // Calculate the range for display
  const startIndex = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  // Shared style for the "Vernieuwen" / "Geschiedenis" outlined buttons.
  const outlinedButtonSx = {
    color: theme.palette.text.tertiary,
    borderColor: theme.palette.border.strong,
    borderRadius: "8px",
    fontWeight: 600,
    textTransform: "none",
    "&:hover": {
      borderColor: theme.palette.border.grey,
      backgroundColor: theme.palette.background.neutral,
    },
  } as const;

  return (
    <Box
      sx={{
        p: { xs: 0, lg: "20px" },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: { xs: "transparent", lg: theme.palette.common.white },
        border: { xs: "none", lg: `1px solid ${theme.palette.divider}` },
        borderRadius: { xs: 0, lg: `${RADIUS.card}px` },
        boxShadow: { xs: "none", lg: theme.customShadows.card },
      }}
    >
      <Box
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
        alignItems={"flex-start"}
        mb={2}
      >
        <Box>
          {!isSmallerScreen && (
            <Typography
              variant="subtitle1"
              sx={{ color: theme.palette.text.primary }}
            >
              {t('components.documentsView.availableDocuments')}
            </Typography>
          )}

          <Typography
            variant="caption"
            color={theme.palette.text.secondary}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {error ? (
              <Typography color="error" component="span">
                {t('components.documentsView.errorPrefix', { error })}
              </Typography>
            ) : totalItems > 0 ? (
              t('components.documentsView.documentsCount', { count: documents.length, total: totalItems })
            ) : (
              t('components.documentsView.noDocuments')
            )}
          </Typography>
        </Box>
        {!isSmallerScreen && (
          <Box display="flex" gap={1} mb={2}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setCurrentPage(1);
                setDocuments([]);
                fetchDocuments(1, false);
              }}
              disabled={isLoading || (!fetchDocumentsOverride && !canRefreshDocuments)}
              startIcon={<Icon name="refresh" size={18} />}
              sx={outlinedButtonSx}
            >
              {t('components.documentsView.refresh')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setHistoryDrawerOpen(true)}
              startIcon={
                <Icon name="clock" size={18} />
              }
              sx={outlinedButtonSx}
            >
              {t('components.documentsView.history')}
            </Button>
          </Box>
        )}
      </Box>
      <Box sx={{ mb: 2 }}>
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t('common.search')}
          width="100%"
          size="medium"
          variant="outlined"
        />
      </Box>

      <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
        {isLoading && documents.length === 0 && <DocumentListSkeleton />}
        <CustomList
          items={transformedDocuments}
          getKey={(doc) => doc.id}
          getTitle={(doc) => doc.name}
          getSubtitle={(doc) =>
            `${doc.size} • ${doc.date} • ${doc.companyName}`
          }
          renderIcon={(doc) => <FileTypeIcon kind={doc.type} size={38} />}
          renderActions={(doc) =>
            renderActionButtons && typeof renderActionButtons === "function"
              ? renderActionButtons(doc.originalDoc)
              : null
          }
          listFooter={
            <Box
              ref={sentinelRef}
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 2,
                minHeight: 40,
              }}
            >
              {isLoadingMore && <CircularProgress size={24} />}
            </Box>
          }
        />
      </Box>
      {/* History Drawer */}
      <HistoryDrawer
        open={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        items={apiHistoryItems}
        selectedItemId=""
        onItemClick={async (item) => {
          setHistoryDrawerOpen(false);
          if (onHistoryItemClick && onHistoryItemClick(item)) return;

          // If the item already carries the job ID, navigate directly without an extra fetch
          if (item.jobId) {
            const route = workflowRoutes[item.workflow ?? ''];
            if (route) {
              navigate(route.path, { state: { jobId: item.jobId, title: item.name } });
              return;
            }
          }

          try {
            const fullData = await workflowService.getHistoryById(item.id);
            if (item.workflow) {
              navigateToWorkflow(item.workflow, fullData, navigate);
            }
          } catch (error) {
            console.error("Failed to load history item:", error);
          }
        }}
        onEdit={() => {}}
        onDelete={() => {}}
        isLoading={isLoadingHistory}
        showCompanyHistory={showCompanyHistory}
        onCompanyHistoryToggle={(enabled) => {
          if (enabled && (!selectedCompanyId || selectedCompanyId === "all")) {
            // Show warning if trying to enable without a valid company selected
            setShowWarning(true);
            return;
          }
          setShowCompanyHistory(enabled);
        }}
      />

      {/* Warning Snackbar */}
      <Snackbar
        open={showWarning}
        autoHideDuration={4000}
        onClose={() => setShowWarning(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowWarning(false)}
          severity="warning"
          sx={{ width: "100%" }}
        >
          {t('components.documentsView.selectCompanyForHistory')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentsView;
